import React, { useEffect, useState, useRef } from 'react';
import { createTemplate } from '../../../service/template';
import CustomToast from '../../Toast';
import apiFunctions from '../../../service/axiosConfig/apiFunctions';
import { baseUrl } from '../../../constants';
import { getTemplateDetails, publishTemplate } from '../../../service/template';
import TemplateHeader from '../../layouts/templateHeader';
import { useSearchParams } from 'react-router-dom';

const CreateTemplate = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const thumbnailInputRef = useRef(null);
    const templateFilesInputRef = useRef(null);
    const [title, setTitle] = useState('');
    const [role, setRole] = useState('');
    const [category, setCategory] = useState('');
    const [lang, setLang] = useState('');
    const [stateValue, setStateValue] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [uploadedThumbnailUrl, setUploadedThumbnailUrl] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [templateFiles, setTemplateFiles] = useState([]);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [templateId, setTemplateId] = useState(null);
    const [isTemplateCreated, setIsTemplateCreated] = useState(false);
    const [status, setStatus] = useState('draft');

    useEffect(() => {
        const templateIdFromUrl = searchParams.get('templateId');
        if (templateIdFromUrl) {
            setTemplateId(templateIdFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (templateId) fetchTemplate(templateId);
    }, [templateId]);

    const fetchTemplate = async (templateId) => {
        try {
            const res = await getTemplateDetails(templateId);
            const data = res.data;
            setTitle(data.title || "");
            setRole(data.role || "");
            setCategory(data.category || "");
            setLang(data.lang || "");
            setStateValue(data.state || "");
            setThumbnailPreview(data?.thumbnail || null);
            setUploadedThumbnailUrl(data?.thumbnail || null);
            setTemplateFiles([]);

            // Convert URL strings to file objects for existing templates
            const existingFiles = (data?.upload_url || []).map((url, index) => {
                if (typeof url === 'string') {
                    // Extract filename from URL with better parsing
                    const urlParts = url.split('/');
                    const lastPart = urlParts[urlParts.length - 1];
                    // Remove query parameters and decode URI components
                    const decodedName = decodeURIComponent(lastPart.split('?')[0]);
                    // Remove timestamp prefixes (like "1640995200000-filename.pdf" or similar patterns)
                    const cleanName = decodedName.replace(/^\d{10,}-/, '').replace(/^[a-f0-9]{32}-/, '');

                    return {
                        url: url,
                        name: cleanName || `Document_${index + 1}`,
                        originalName: cleanName
                    };
                }
                return url; // If it's already an object
            });

            setUploadedFiles(existingFiles);
            setIsTemplateCreated(true);
            setStatus(data.status || 'draft');
        } catch (err) {
            console.error(err);
        }
    };


    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setThumbnailFile(file);
            setThumbnailPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    const handleUploadThumbnail = async () => {
        if (!thumbnailFile) return;

        try {
            const response = await apiFunctions.uploadDocFiles(`${baseUrl}uploadFiles`, thumbnailFile);
            const uploadedUrl = response?.data?.urls[0];
            setUploadedThumbnailUrl(uploadedUrl);
            setPreviewUrl(uploadedUrl);
            setThumbnailFile(null);
            if (thumbnailInputRef.current) {
                thumbnailInputRef.current.value = '';
            }
            CustomToast.success("Thumbnail uploaded successfully!");
        } catch (error) {
            CustomToast.error("Thumbnail upload failed.");
        }
    };

    const handleTemplateChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const validFiles = files.filter(file => file.size <= 50 * 1024 * 1024);
        const invalidFiles = files.filter(file => file.size > 50 * 1024 * 1024);

        if (invalidFiles.length > 0) {
            alert(`${invalidFiles.length} file(s) exceeded 50MB limit and were skipped.`);
        }

        setTemplateFiles(validFiles);

        const imageFile = validFiles.find(file => file.type.startsWith("image/"));
        if (imageFile) {
            setTemplatePreview(URL.createObjectURL(imageFile));
        } else {
            setTemplatePreview(null);
        }
    };

    const handleRemoveAllTemplates = () => {
        setTemplateFiles([]);
        setTemplatePreview(null);
        if (templateFilesInputRef.current) {
            templateFilesInputRef.current.value = '';
        }
    };

    const handleUploadTemplate = async () => {
        if (!templateFiles.length) {
            alert("No documents selected.");
            return;
        }
        setIsUploading(true);
        try {
            const response = await apiFunctions.uploadFiles(`${baseUrl}uploadFiles`, templateFiles);
            const urls = response?.data?.urls || [];

            // Create file objects with both URL and original file name
            const fileObjects = urls.map((url, index) => ({
                url: url,
                name: templateFiles[index]?.name || `Document_${index + 1}`,
                originalName: templateFiles[index]?.name
            }));

            setUploadedFiles(prev => [...prev, ...fileObjects]);
            setTemplateFiles([]);
            setTemplatePreview(null);
            if (templateFilesInputRef.current) {
                templateFilesInputRef.current.value = '';
            }
            CustomToast.success("Documents uploaded successfully.");
        } catch (error) {
            console.error("Upload error:", error);
            CustomToast.error("Error uploading documents.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSaveOrPublish = async (statusType) => {
        setIsSaving(true);

        // Convert file objects to URLs for the API payload
        const fileUrls = uploadedFiles.map(file =>
            typeof file === 'string' ? file : file.url
        );

        const payload = {
            title,
            role,
            category,
            status: statusType,
            is_publish: statusType === "published" ? true : false,
            publish_date:
                statusType === "published" ? new Date().toISOString() : null,
            thumbnail: uploadedThumbnailUrl || previewUrl,
            upload_url: fileUrls
        };

        payload.lang = (lang && lang.trim() !== '') ? lang : null;
        payload.state = (stateValue && stateValue.trim() !== '') ? stateValue : null;

        try {
            const res = await createTemplate(payload);
            if (res?.success && res?.data?._id) {
                setTemplateId(res.data._id);
                setIsTemplateCreated(true);
                setStatus(statusType);
                setSearchParams({ templateId: res.data._id });
                fetchTemplate(res.data._id);
                CustomToast.success("Template saved successfully");
                setTimeout(() => {
                    window.location.reload(true);
                }, 1000);
            } else {
                console.error("Error saving Template:", res);
                handleValidationMessages(res?.message);
            }
        } catch (error) {
            console.error(error);
            CustomToast.error("An unexpected error occurred while saving the template.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleValidationMessages = (message) => {
        if (!message) {
            CustomToast.error('Unexpected error occurred.');
            return;
        }

        if (message.includes('"title" is not allowed to be empty')) {
            CustomToast.error('Title is required.');
        } else if (message.includes('"role" is not allowed to be empty')) {
            CustomToast.error('Role is required.');
        } else if (message.includes('"category" is not allowed to be empty')) {
            CustomToast.error('Category is required.');
        } else if (message.includes('"lang" is not allowed to be empty')) {
            CustomToast.error('Language is required.');
        } else if (message.includes('"state" is not allowed to be empty')) {
            CustomToast.error('State is required.');
        } else if (message.includes('Thumbnail must be a string')) {
            CustomToast.error('Thumbnail is required');
        } else if (message.includes('least one file must be uploaded')) {
            CustomToast.error('At least one template file must be uploaded');
        } else {
            CustomToast.error('Error occurred while saving the Template.');
        }
    };

    const handleTogglePublishStatus = async () => {
        setIsPublishing(true);

        try {

            if (!isTemplateCreated) {
                CustomToast.error("Please save the template first before publishing.");
                return;
            }

            // Toggle between published and draft
            const newStatus = status === 'published' ? 'draft' : 'published';
            const response = await handleUpdateTemplateStatus(templateId, newStatus);

            if (response?.data?.status) {
                setStatus(newStatus);
                const actionMessage = newStatus === 'published' ? 'published' : 'moved to draft';
                CustomToast.success(`Template ${actionMessage} successfully`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsPublishing(false);
        }
    };
    const handleUpdateTemplateStatus = async (templateId, newStatus) => {
        const data = {
            is_publish: newStatus === 'published',
            publish_date: new Date().toISOString().split('T')[0],
            status: newStatus
        };

        try {
            const response = await publishTemplate(templateId, data);
            return response;
        } catch (error) {
            CustomToast.error(`Error while updating Template status`);
            return error;
        }
    };

    return (
        <>
            <TemplateHeader
                isEdit={false}
                isSaving={isSaving}
                isPublishing={isPublishing}
                onSaveDraft={() => handleSaveOrPublish("draft")}
                onPublish={handleTogglePublishStatus}
                isTemplateCreated={isTemplateCreated}
                isSaveDisabled={false}
                currentStatus={status}
            />
            {/* Sidebar */}
            <aside className="course_details_sidebar sidebar px-4 pt-5" id="admin-dash-nav">
                <div className='d-flex flex-column justify-content-between px-3 course_details'>
                    <div className="row">
                        <h5 className="font-20 mb-4 fw-bold">Upload file</h5>
                        <label className="fw-bold mb-2">Upload Thumbnail</label>

                        <div
                            className="border rounded p-3 w-100 position-relative"
                            style={{
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderColor: '#dee2e6',
                            }}
                        >
                            {!thumbnailPreview ? (

                                <div
                                    className="d-flex flex-column justify-content-center align-items-center w-100"
                                    style={{
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa',
                                        border: '2px dashed #ccc',
                                        borderRadius: '8px',
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <img src="/images/File.svg" alt="file" style={{ width: '40px', marginBottom: '8px' }} />
                                    <p className="m-0 text-muted">Choose File</p>

                                    <input
                                        ref={thumbnailInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleThumbnailChange}
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                        style={{
                                            position: 'absolute',
                                            height: '100%',
                                            width: '100%',
                                            top: 0,
                                            left: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            zIndex: 5,
                                        }}
                                    />
                                </div>

                            ) : (
                                <div
                                    className="position-relative w-100 d-flex justify-content-center align-items-center"
                                    style={{
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa',
                                        border: '2px dashed #ccc',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <img
                                        src={thumbnailPreview}
                                        alt="Preview"
                                        style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveThumbnail}
                                        className="position-absolute btn btn-link p-0 m-0"
                                        style={{ top: '5px', right: '5px', zIndex: 10 }}
                                    >
                                        <img src="/images/delete.png" alt="remove" style={{
                                            width: '25px',
                                            backgroundColor: '#fff'
                                        }} />
                                    </button>
                                </div>
                            )}

                            {thumbnailFile && (
                                <>
                                    <p className="text-center mt-3 text-break" style={{ fontSize: '14px', color: '#333' }}>
                                        {thumbnailFile.name}
                                    </p>
                                    <div className="d-flex justify-content-center">
                                        <button
                                            type="button"
                                            className="btn btn-primary mt-1 px-5 w-75 py-2 "
                                            onClick={handleUploadThumbnail}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </>

                            )}
                        </div>
                    </div>

                    <div className='row'>
                           <label className="fw-bold mb-2 mt-3">Upload Templates </label>
                        {/* Upload Card Box */}
                        <div
                            className="border rounded p-3 w-100 position-relative"
                            style={{
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderColor: '#dee2e6',
                                backgroundColor: '#fff',
                            }}
                        >
                            {/* Upload Box */}
                            <div
                                className="d-flex flex-column justify-content-center align-items-center w-100"
                                style={{
                                    minHeight: '150px',
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                }}
                            >
                                {/* If preview exists (uploaded file) */}
                                {templatePreview ? (
                                    <img
                                        src={templatePreview}
                                        alt="Preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '150px',
                                            objectFit: 'contain',
                                            borderRadius: '6px',
                                        }}
                                    />
                                ) : (
                                    <>
                                        <img src="/images/File.svg" alt="file" style={{ width: '40px', marginBottom: '8px' }} />
                                        <p className="m-0 text-muted text-center">Choose File</p>
                                    </>
                                )}

                                <input
                                    ref={templateFilesInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleTemplateChange}
                                    style={{
                                        position: 'absolute',
                                        height: '100%',
                                        width: '100%',
                                        top: 0,
                                        left: 0,
                                        opacity: 0,
                                        cursor: 'pointer',
                                        zIndex: 5,
                                    }}
                                />
                            </div>

                            {/* Delete Icon - Top Right (like thumbnail) */}
                            {templateFiles.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleRemoveAllTemplates}
                                    className="position-absolute btn btn-link p-0 m-0 "
                                    style={{
                                        top: '20px',
                                        right: '20px',
                                        zIndex: 10,
                                    }}
                                >
                                    <img
                                        src="/images/delete.png"
                                        alt="remove"
                                        style={{
                                            width: '25px',
                                           
                                            backgroundColor: '#fff'
                                        }} />
                                </button>

                            )}

                            {/* File Name & Upload Btn Inside Card */}
                            {templateFiles.length > 0 && (
                                <div className="text-center mt-3">
                                    <ul className='small' style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {templateFiles.map((file, idx) => (
                                            <li key={idx} className='text-truncate' title={file.name}>
                                                {file.name}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type='button'
                                        className='btn btn-primary mt-3 px-5 '
                                        onClick={handleUploadTemplate}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? "Uploading..." : "Upload Documents"}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Uploaded Files Display */}
                        {uploadedFiles?.length > 0 && (
                            <div>
                                <h6 className='my-3'>Uploaded Files:</h6>
                                {uploadedFiles.map((file, index) => {
                                    // Handle both string URLs and file objects
                                    const fileUrl = typeof file === 'string' ? file : file?.url;

                                    // Extract file name from URL or use stored name
                                    let fileName;
                                    if (file?.name || file?.originalName) {
                                        fileName = file.name || file.originalName;
                                    } else if (fileUrl) {
                                        // Extract filename from URL
                                        const urlParts = fileUrl.split('/');
                                        const lastPart = urlParts[urlParts.length - 1];
                                        // Remove query parameters and decode URI components
                                        const decodedName = decodeURIComponent(lastPart.split('?')[0]);
                                        // Remove timestamp prefixes (like "1640995200000-filename.pdf")
                                        const cleanName = decodedName.replace(/^\d{10,}-/, '');
                                        fileName = cleanName || `Document_${index + 1}`;
                                    } else {
                                        fileName = `Document_${index + 1}`;
                                    }

                                    const fileId = typeof file === 'object' ? file?._id : null;

                                    // Determine file type and icon
                                    const getFileTypeAndIcon = (url, name) => {
                                        const fileExtension = url?.split('.').pop()?.toLowerCase() || name?.split('.').pop()?.toLowerCase();

                                        if (fileExtension === 'pdf') {
                                            return { type: 'PDF', icon: '📄', color: '#757575' };
                                        } else if (['doc', 'docx'].includes(fileExtension)) {
                                            return { type: 'Word', icon: '📝', color: '#757575' };
                                        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension)) {
                                            return { type: 'Image', icon: '🖼️', color: '#757575' };
                                        } else if (['ppt', 'pptx'].includes(fileExtension)) {
                                            return { type: 'PowerPoint', icon: '📊', color: '#757575' };
                                        } else if (['xls', 'xlsx'].includes(fileExtension)) {
                                            return { type: 'Excel', icon: '📈', color: '#757575' };
                                        } else {
                                            return { type: 'Document', icon: '📄', color: '#757575' };
                                        }
                                    };

                                    const fileInfo = getFileTypeAndIcon(fileUrl, fileName);

                                    return (
                                        <div key={fileId || index} className="position-relative my-2 p-2 border rounded">
                                            <div className="d-flex align-items-center">
                                                <span style={{ fontSize: '20px', marginRight: '10px' }}>
                                                    {fileInfo.icon}
                                                </span>
                                                <div className="flex-grow-1">
                                                    <div>
                                                        <strong style={{ color: '#333', fontSize: '14px' }}>
                                                            {fileName}
                                                        </strong>
                                                    </div>
                                                    <small className="text-muted">{fileInfo.type} Document</small>
                                                    <div>
                                                        <a
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none small"
                                                            style={{ color: fileInfo.color }}
                                                        >
                                                            📎 Open File
                                                        </a>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
                                                        setUploadedFiles(updatedFiles);
                                                    }}
                                                    className="btn btn-sm btn-outline ms-2"
                                                    style={{ padding: '2px 8px' }}
                                                    title="Remove file"
                                                >
                                                    <img src="/images/delete.png" alt="remove" className="remove-pic" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className='course_main'>
                 <h5 className="font-20 fw-bold text-black mb-4">Template Details</h5>
                <div className="container-fluid">
                    <form onSubmit={handleSaveOrPublish}>
                        {/* Title */}
                        <div className="mb-3">
                            <label className="form-label fw-bold primary_color">Template Name<span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>

                        {/* Role */}
                        <div className="mb-3">
                            <label className="form-label fw-bold primary_color">Select Role<span className="text-danger">*</span></label>
                            <select
                                className="form-select"
                                value={role}
                                onChange={(e) => {
                                    setRole(e.target.value);
                                    setCategory('');
                                }}
                            >
                                <option value="">Select</option>
                                <option value="admin">Org Admin</option>
                                <option value="ic">IC User</option>
                            </select>
                        </div>

                        {/* Category */}
                        <div className="mb-3">
                            <label className="form-label fw-bold primary_color">Select Compliance Module<span className="text-danger">*</span></label>
                            <select
                                className="form-select"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <option value="">Select</option>

                                {role === 'ic' ?
                                    <>
                                        <option value="filing">Filing</option>
                                        <option value="inquiry">Inquiry</option>
                                        <option value="reports">Reports</option>
                                    </>
                                    : <><option value="policy">Policy</option>
                                        <option value="poster">Poster</option>
                                        <option value="ic_template">IC Template</option>
                                        <option value="email_campaign">Email Campaign</option>
                                        <option value="annual_report">Annual Report</option> </>}
                            </select>
                        </div>

                        {/* Language */}
                        <div className="mb-3">
                            <label className="form-label fw-bold primary_color">Select Language</label>
                            <select
                                className="form-select"
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                            >
                                <option value="">Select</option>
                                <option value="english">English</option>
                                <option value="hindi">Hindi</option>
                                <option value="marathi">Marathi</option>
                            </select>
                        </div>

                        {/* State */}
                        <div className="mb-3">
                            <label className="form-label fw-bold primary_color">Select State</label>
                            <select
                                className="form-select"
                                value={stateValue}
                                onChange={(e) => setStateValue(e.target.value)}
                            >
                                <option value="">Select</option>
                                <option value="maharashtra">Maharashtra</option>
                                <option value="karnataka">Karnataka</option>
                                <option value="tamil nadu">Tamil Nadu</option>
                                <option value="delhi">Delhi</option>
                                <option value="uttar pradesh">Uttar Pradesh</option>
                                <option value="gujarat">Gujarat</option>
                                <option value="rajasthan">Rajasthan</option>
                                <option value="kerala">Kerala</option>
                                <option value="telangana">Telangana</option>
                                <option value="punjab">Punjab</option>
                            </select>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default CreateTemplate;